'use client';

import { cn } from '@/lib/utils';
import { motion } from 'motion/react';
import { forwardRef } from 'react';

const MotionDiv: any = motion.div;

export interface GlassCardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'elevated' | 'subtle' | 'gradient';
  glowEffect?: boolean;
  liquidGlass?: boolean;
  interactive?: boolean;
  children: React.ReactNode;
}

const GlassCard = forwardRef<HTMLDivElement, GlassCardProps>(
  ({
    className,
    variant = 'elevated',
    glowEffect = true,
    liquidGlass = true,
    interactive = true,
    children,
    ...props
  }, ref) => {
    const baseStyles = cn(
      // Base card styles
      'relative rounded-xl',
      
      // Glass morphism variants
      {
        'glass-morphism': variant === 'default' || variant === 'subtle',
        'elevated-glass-card': variant === 'elevated',
        'bg-gradient-to-br from-bg-primary/80 via-bg-primary/60 to-bg-primary/40 dark:from-white/10 dark:via-white/5 dark:to-transparent backdrop-blur-md border border-border-primary shadow-premium shadow-black/20': variant === 'gradient',
      },
      
      // Interactive effects
      interactive && [
        'transition-shadow-subtle transition-opacity duration-500',
        'hover:shadow-premium hover:shadow-black/30'
      ],
      
      className
    );

    return (
      <MotionDiv
        ref={ref}
        className={baseStyles}
        whileHover={interactive ? { y: -4 } : undefined}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
        {...props}
      >
        {/* Liquid glass overlay */}
        {liquidGlass && (
          <div className="absolute inset-0 rounded-inherit bg-gradient-to-br from-white/20 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
        )}
        
        {/* Content */}
        <div className="relative z-10">
          {children}
        </div>
        
        {/* Glass reflection */}
        {liquidGlass && (
          <div className="absolute top-0 left-0 right-0 h-1/3 rounded-t-inherit bg-gradient-to-b from-white/30 to-transparent opacity-50" />
        )}
      </MotionDiv>
    );
  }
);

GlassCard.displayName = 'GlassCard';

export default GlassCard;
